// ============================================================
// MyWorkParts.tsx
// Presentational building blocks for the "My work" execution queue, ported
// 1:1 from the approved mockup (casevo-mywork-B-focus.html). Every size,
// colour, radius, shadow and letter-spacing below is copied from the mockup's
// CSS as a Tailwind arbitrary value (not the nearest utility), with the
// mockup's dark-theme value as an explicit dark: variant. Tokens are used
// where the mockup var IS the app token (surface / border / forest / brand /
// mid / mint / input / text-soft / gold); the mockup-only tones are literals:
//   hair     #e1ece6 / #34463b      track   #e8f1ec / #31443a
//   red      #c23b2c / #f3a094      redbg   #fbeae7 / rgba(243,160,148,.14)
//   redline  #f1c9c2 / rgba(243,160,148,.28)   rowhover #f6faf8 / #2b3b31
//   goldink  #3a2e08                green   #2d6a4f / #7fd1a8
// No store / network access here — MyWork.tsx wires the data + actions.
// ============================================================
import React, { useEffect, useRef } from 'react'
import type { Task, TaskPriority } from '../../types/tasks.types'
import type { FirmUser } from '../../types/user.types'
import { PRIORITY_LABELS } from '../shared/TaskRow'
import UserAvatar from '../shared/UserAvatar'

// ── Shared class recipes (mockup → Tailwind) ─────────────────────────
// .card
export const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px] shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
export const HAIR = 'border-casevo-border-soft'
export const RED = 'text-[#c23b2c] dark:text-[#f3a094]'
export const REDBG = 'bg-[#fbeae7] dark:bg-[rgba(243,160,148,0.14)]'
// !important so it beats CARD's border-casevo-border (same specificity, later in the sheet).
export const REDLINE = '!border-[#f1c9c2] dark:!border-[rgba(243,160,148,0.28)]'
export const TRACK = 'bg-[#e8f1ec] dark:bg-[#31443a]'
// .pop
export const POP = 'absolute z-30 bg-casevo-surface border border-casevo-border rounded-[12px] p-[6px] shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]'
export const POP_HEAD = 'text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft px-[10px] pt-[8px] pb-[6px] font-semibold'
export const POP_OPT = 'w-full flex items-center gap-[10px] px-[10px] py-[8px] rounded-[8px] text-[13px] text-casevo-forest text-left hover:bg-casevo-input focus:outline-none focus-visible:bg-casevo-input'

// ── Icons (Feather line icons, currentColor — design doc §6) ─────────
const P: Record<string, React.ReactNode> = {
  check: <polyline points="20 6 9 17 4 12" />,
  folder: <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  chev: <polyline points="6 9 12 15 18 9" />,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
  arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  refresh: <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
  trend: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>,
  more: <><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></>,
  inbox: <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
}
export type IconName = keyof typeof P
export const Ic: React.FC<{ n: IconName; className?: string; strokeWidth?: number }> = ({ n, className = 'w-[14px] h-[14px]', strokeWidth = 2 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={`shrink-0 ${className}`} aria-hidden="true">{P[n]}</svg>
)

// Close a popover on outside-click / Escape.
export const useDismiss = (ref: React.RefObject<HTMLElement | null>, onClose: () => void, open = true): void => {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [ref, onClose, open])
}

// ── PRIORITY PILL (designed here) ─────────────────────────────────────
// Three signal bars + label, so it reads WITHOUT colour (bar count = level).
//   High   — red tint, filled, all 3 bars           (.pri.high)
//   Normal — quiet: no fill, mid-green text, 2 bars  (.pri.normal)
//   Low    — hairline outline, soft text, 1 bar      (.pri.low)
// Never gold (gold is the restrained accent). The caret appears on row hover
// when the pill is clickable (quick reprioritise).
export const PriorityBars: React.FC<{ p: TaskPriority; all?: boolean }> = ({ p, all = false }) => {
  const lit = all ? 3 : p === 'high' ? 3 : p === 'normal' ? 2 : 1
  const h = ['h-[5px]', 'h-[8px]', 'h-[11px]']
  return (
    <span className="inline-flex items-end gap-[1.5px] h-[11px]" aria-hidden="true">
      {h.map((cls, i) => (
        <i key={i} className={`block w-[3px] rounded-[1px] bg-current ${cls} ${i < lit ? (p === 'high' || all ? 'opacity-100' : 'opacity-90') : 'opacity-25'}`} />
      ))}
    </span>
  )
}
const PRI_CLS: Record<TaskPriority, string> = {
  high: `${REDBG} ${RED} font-semibold`,
  normal: 'bg-transparent text-casevo-mid font-medium',
  low: 'text-casevo-text-soft border border-casevo-border font-medium',
}
export const PriorityPill: React.FC<{ p: TaskPriority; caret?: boolean; onClick?: (e: React.MouseEvent) => void; disabledTitle?: string }> = ({ p, caret = false, onClick, disabledTitle }) => {
  const cls = `inline-flex items-center gap-[6px] h-[24px] pl-[7px] pr-[9px] rounded-full text-[11.5px] whitespace-nowrap leading-none ${PRI_CLS[p]}`
  const inner = (<><PriorityBars p={p} />{PRIORITY_LABELS[p]}{caret && <Ic n="chev" className="w-[11px] h-[11px] -ml-[2px] opacity-0 group-hover/row:opacity-70 transition-opacity" />}</>)
  if (!onClick) return <span className={cls} title={disabledTitle ?? `${PRIORITY_LABELS[p]} priority`}>{inner}</span>
  return (
    <button type="button" onClick={onClick} className={`${cls} focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid`}
      title={`${PRIORITY_LABELS[p]} priority — click to change`} aria-haspopup="menu">{inner}</button>
  )
}

// Quick-reprioritise popover (.pop with H / N / L keys).
export const PriorityMenu: React.FC<{ current: TaskPriority; onPick: (p: TaskPriority) => void; onClose: () => void; hint?: string; className?: string }> = ({ current, onPick, onClose, hint, className = 'right-[64px] top-[50px]' }) => {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, onClose)
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const k = e.key.toLowerCase()
      if (k === 'h') onPick('high')
      else if (k === 'n') onPick('normal')
      else if (k === 'l') onPick('low')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onPick])
  return (
    <div ref={ref} role="menu" className={`${POP} w-[226px] ${className}`} onClick={(e) => e.stopPropagation()}>
      <div className={POP_HEAD}>Priority</div>
      {(['high', 'normal', 'low'] as TaskPriority[]).map((p) => (
        <button key={p} type="button" role="menuitemradio" aria-checked={current === p} onClick={() => onPick(p)}
          className={`${POP_OPT} ${current === p ? 'bg-casevo-input' : ''}`}>
          <PriorityPill p={p} />
          {current === p
            ? <span className="ml-auto text-casevo-brand"><Ic n="check" strokeWidth={2.6} /></span>
            : <span className="ml-auto font-mono text-[10.5px] text-casevo-text-soft border border-casevo-border rounded-[4px] px-[5px] py-px">{p[0].toUpperCase()}</span>}
        </button>
      ))}
      {hint && <div className="text-[11px] text-casevo-text-soft px-[10px] pt-[6px] pb-[6px]">{hint}</div>}
    </div>
  )
}

// ── People ─────────────────────────────────────────────────────────────
export const userName = (u: FirmUser | undefined): string =>
  u ? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'Unknown') : 'Former member'
// .av — a person's avatar (shared UserAvatar) in a 2px ring; the owner gets a
// brand outline when the task is shared. `you` only changes the tooltip.
export const PersonDot: React.FC<{ user?: FirmUser; you?: boolean; owner?: boolean; size?: number; ring?: string; title?: string }> = ({ user, you = false, owner = false, size = 26, ring = 'border-casevo-surface', title }) => {
  // ★ The person's avatar is the shared UserAvatar (photo / colour / style from
  // their account — the same face as everywhere else); this only adds the
  // stack ring + the owner outline around it. (The old hand-built dot tinted
  // the colour itself and painted "you" gold, so it never matched.)
  const t = title ?? `${you ? 'You' : userName(user)}${owner ? ' (owner)' : ''}`
  const avSize = size <= 24 ? 'pill' : size <= 27 ? 'rail' : 'sm'
  return (
    <span role="img" aria-label={t} title={t}
      className={`inline-flex rounded-full shrink-0 border-2 ${ring} ${owner ? 'shadow-[0_0_0_1.5px_var(--casevo-brand)]' : ''}`}>
      <UserAvatar userId={user?.id} fallbackName={userName(user)} size={avSize} title={false} />
    </span>
  )
}

// .ppl — overlapping dots (-7px), owner ringed when shared, "Firm" chip.
export const PeopleChips: React.FC<{ ids: string[]; firmWide?: boolean; byId: Map<string, FirmUser>; currentUserId: string; ring?: string }> = ({ ids, firmWide, byId, currentUserId, ring }) => (
  <span className="flex items-center">
    {ids.map((id, i) => (
      <span key={id} className={i === 0 ? '' : '-ml-[7px]'}>
        <PersonDot user={byId.get(id)} you={id === currentUserId} owner={i === 0 && ids.length > 1} ring={ring} />
      </span>
    ))}
    {firmWide && (
      <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold text-casevo-brand dark:text-[#7fd1a8] bg-casevo-mint rounded-full pl-[6px] pr-[8px] py-[3px] ml-[6px]">
        <Ic n="globe" className="w-[12px] h-[12px]" />Firm
      </span>
    )}
  </span>
)

// ── Group heading (.gh) ────────────────────────────────────────────────
export type GroupTone = 'overdue' | 'today' | 'plain'
export const GroupHeading: React.FC<{ label: string; count: number; tone?: GroupTone; when?: string; right?: React.ReactNode }> = ({ label, count, tone = 'plain', when, right }) => {
  const col = tone === 'overdue' ? RED : tone === 'today' ? 'text-casevo-brand' : 'text-[#6f7d73] dark:text-[#8aa899]'
  return (
    <div className={`flex items-center gap-[9px] text-[12px] font-bold tracking-[0.16em] uppercase mx-[2px] mb-[10px] ${col}`}>
      <span className="w-[7px] h-[7px] rounded-full bg-current" />
      {label}
      <span className={`font-semibold tracking-[0.04em] ${tone === 'overdue' ? RED : 'text-casevo-text-soft'}`}>{count}</span>
      {when && <span className="font-medium tracking-[0.04em] normal-case text-casevo-text-soft text-[12.5px]">{when}</span>}
      <span className="flex-1 h-px bg-casevo-border-soft" />
      {right}
    </div>
  )
}

// ── Toast (.toast) ─────────────────────────────────────────────────────
export const Toast: React.FC<{ text: React.ReactNode; action?: { label: string; onClick: () => void }; error?: boolean }> = ({ text, action, error = false }) => (
  <div role="status" aria-live="polite"
    className="fixed left-1/2 bottom-[28px] -translate-x-1/2 z-[60] flex items-center gap-[14px] rounded-[10px] pl-[12px] pr-[14px] py-[11px] text-[13.5px] shadow-[0_14px_36px_rgba(0,0,0,0.35)] bg-[#12261b] text-[#e8f0ea] dark:bg-[#e8f0ea] dark:text-[#12261b] max-w-[calc(100vw-32px)]">
    <span className={`w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0 ${error ? 'bg-[#e5534b] text-white' : 'bg-[#52a87f] text-[#0d1a13]'}`}>
      <Ic n={error ? 'alert' : 'check'} className="w-[12px] h-[12px]" strokeWidth={3.2} />
    </span>
    <span>{text}</span>
    {action && (
      <button type="button" onClick={action.onClick}
        className="text-casevo-gold dark:text-[#8a6d18] font-semibold tracking-[0.06em] uppercase text-[12px] pl-[12px] border-l border-[rgba(255,255,255,0.14)] dark:border-[rgba(0,0,0,0.12)] focus:outline-none casevo-focus">
        {action.label}
      </button>
    )}
  </div>
)

// ── Admin badge (.adminbadge) ───────────────────────────────────────────
export const AdminBadge: React.FC<{ label?: string }> = ({ label = 'Admin' }) => (
  <span className="inline-flex items-center gap-[5px] text-[10px] tracking-[0.14em] uppercase font-semibold text-casevo-text-soft border border-casevo-border rounded-[4px] px-[6px] py-[2px]">
    <Ic n="shield" className="w-[11px] h-[11px]" />{label}
  </span>
)

export type { Task }
